import { Alert, Checkbox, Empty, Flex, Input, Modal, Skeleton, Typography } from 'antd'
import { useMemo, useState } from 'react'
import { useCustomerAttachments } from '../attachments/attachmentsQueries'
import {
  attachmentSubtitle,
  canAcceptMore,
  ProposalAttachment
} from './proposalAttachmentsModel'

type Props = {
  open: boolean
  customerId: number
  alreadyAttached: ProposalAttachment[]
  onClose: () => void
  onAttach: (attachments: ProposalAttachment[]) => void
}

const AttachExistingFilesModal = ({ open, customerId, alreadyAttached, onClose, onAttach }: Props) => {
  const query = useCustomerAttachments(customerId)
  const [selected, setSelected] = useState<string[]>([])
  const [search, setSearch] = useState('')

  const attachedIds = alreadyAttached.map(x => x.attachmentId)

  const available = useMemo(() => (query.data ?? [])
    .filter(x => !attachedIds.includes(x.id))
    .filter(x => search.trim().length === 0 || x.name.toLowerCase().includes(search.trim().toLowerCase())),
    [query.data, attachedIds, search])

  const toggle = (id: string) => setSelected(x =>
    x.includes(id) ? x.filter(y => y !== id) : [...x, id])

  const close = () => {
    setSelected([])
    setSearch('')
    onClose()
  }

  const attach = () => {
    const chosen: ProposalAttachment[] = (query.data ?? [])
      .filter(x => selected.includes(x.id))
      .map(x => ({
        attachmentId: x.id,
        filename: x.filename ?? x.name,
        name: x.name,
        size: x.sizeInBytes ?? 0
      }))
    onAttach(chosen)
    close()
  }

  const wouldExceed = !canAcceptMore(alreadyAttached, selected.length)

  return <Modal
    open={open}
    title="Attach files from this customer"
    onCancel={close}
    onOk={attach}
    okText={selected.length > 0 ? `Attach ${selected.length}` : 'Attach'}
    okButtonProps={{ disabled: selected.length === 0 || wouldExceed }}
    width={640}
  >
    <Flex vertical gap="small">
      <Typography.Text type="secondary">
        These files are already on the customer record. Attaching one links it to this proposal &mdash; it is not uploaded again.
      </Typography.Text>

      {wouldExceed && <Alert
        type="warning"
        showIcon
        message="That would put this proposal over the attachment limit."
      />}

      <Input.Search
        placeholder="Search by name"
        value={search}
        onChange={e => setSearch(e.target.value)}
        allowClear
      />

      {query.isLoading && <Skeleton active paragraph={{ rows: 4 }} />}

      {!query.isLoading && available.length === 0 && <Empty
        description={search.trim().length > 0
          ? 'No files match that search.'
          : 'This customer has no other files to attach.'}
      />}

      {available.map(attachment => <Checkbox
        key={attachment.id}
        checked={selected.includes(attachment.id)}
        onChange={() => toggle(attachment.id)}
      >
        <span style={{ fontWeight: 600 }}>{attachment.name}</span>
        <span style={{ color: '#667085', marginLeft: '8px', fontSize: '12px' }}>
          {attachmentSubtitle({
            attachmentId: attachment.id,
            filename: attachment.filename ?? attachment.name,
            name: attachment.name,
            size: attachment.sizeInBytes ?? 0
          })}
        </span>
      </Checkbox>)}
    </Flex>
  </Modal>
}

export default AttachExistingFilesModal
